import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material//Close';
import { Collapse, IconButton } from '@mui/material';
import Alert from '@mui/material/Alert';
import { isEmpty } from 'lodash';
import { CapTableDetailsModel, MeasurementDate, ProFormaCapTableModel } from 'api';
import { CUSTOM_BREAKPOINTS_REFERENCE_TITLE, CUSTOM_BREAKPOINTS_REFERENCE_TYPE } from 'common/constants/documents';
import { FIX_BEFORE_CONTINUE } from 'common/constants/messages/validations';
import { BREAKPOINTS_PAGE_VALUE, CAP_TABLE_PAGE_KEY } from 'common/constants/notes';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { ExtendedFabButton } from 'components/Buttons';
import { ConfirmationDialog } from 'components/Dialogs';
import WidgetDrawer from 'components/Drawer/widget-drawer/WidgetDrawer';
import { GridSkeleton } from 'components/Grid';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import DialogContext from 'context/DialogContext';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { ConfirmationSaveFinal, ProformaCaptableDialog } from 'pages/CapTable/cap-table/components';
import { CaptableHk, useDocuments, useKeyEventForFabButton } from 'services/hooks';
import { useNotes } from 'services/hooks/notes';
import useSpreadsheetNotes from 'services/hooks/spreadsheetNotes/useSpreadsheetNotes';
import useCustomBreakpointsTableData from './useCustomBreakpointsTableData';
import { BreakpointColumn, BreakpointFormat, CustomBreakpointsData, CustomBreakpointsSaveState } from '../../types/breakpoint-analysis.models';
import conditions from '../common/conditions';

interface CustomBreakpointsProps {
  captableId: number;
  hideGrid: boolean;
  selectedMeasurementDate: MeasurementDate;
  customBreakpointColumns: BreakpointColumn[];
  saveAsNewProforma: (props: ProFormaCapTableModel) => void;
  format: BreakpointFormat;
  isAllocationFinal: boolean;
  isDisabled: boolean;
  customBreakpointsGridRef: React.RefObject<CustomBreakpointsData | null>;
  onSaveStateChange?: (state: CustomBreakpointsSaveState | null) => void;
}

const CustomBreakpoints = ({
  captableId,
  customBreakpointsGridRef,
  hideGrid,
  selectedMeasurementDate,
  customBreakpointColumns,
  saveAsNewProforma,
  format,
  isAllocationFinal,
  isDisabled,
  onSaveStateChange,
}: CustomBreakpointsProps) => {
  const [{ captableInfo }] = useStore() as unknown as UseStoreValues;
  const { useUpdateCaptableCustomBreakpoints } = CaptableHk;
  const initialFormValues = useMemo(
    () => ({
      isValid: true,
      name: `${captableInfo?.name} Proforma`,
      values: {} as Record<string, any>,
      errors: {},
    }),
    [captableInfo]
  );

  const {
    spreadsheet: CustomBreakpointsDetailSheet,
    addCustomColumn,
    deleteCustomColumn,
  } = useCustomBreakpointsTableData({
    customBreakpointColumns,
    captableInfo: captableInfo as CapTableDetailsModel,
    format,
    isDisabled,
  });

  const [spreadsheets, setSpreadsheets] = useState([CustomBreakpointsDetailSheet]);
  const [isAlertVisible, setIsAlertVisible] = useState(false);
  const [openProformaDialog, setOpenProformaDialog] = useState(false);
  const [formState, setFormState] = useState(initialFormValues);
  const { updateCustomBreakpoints } = useUpdateCaptableCustomBreakpoints();
  const { filesToSave, setFilesToSave, addReferenceForExistingDocuments } = useDocuments();
  const {
    spreadsheetNotes,
    setSpreadsheetNotes,
    saveSpreadsheetNotes,
    hasChanged: spreadsheetNotesHasChanged,
    handleOnDeleteNote: handleOnDeleteSpreadsheetNote,
  } = useSpreadsheetNotes();
  const { notes, setNotes, notesHasChanged, saveNotes, onAddNote, onUpdateNotes, onDeleteNote } = useNotes();
  const [isDisplayingRowNumbers, setIsDisplayingRowNumbers] = useState(false);
  const { showDialog } = useContext(DialogContext);

  const { onChange, workbook } = useWorkbook(spreadsheets);

  useEffect(() => {
    if (CustomBreakpointsDetailSheet?.yLength > 0) {
      setSpreadsheets([CustomBreakpointsDetailSheet]);
    }
  }, [captableInfo, CustomBreakpointsDetailSheet]);

  useEffect(() => {
    if (hideGrid) {
      // Hide alert, if present, when disabling the custom breakpoints
      setIsAlertVisible(false);
    }
  }, [hideGrid]);

  const getGridData = useCallback((): Partial<CapTableDetailsModel> => {
    const breakpointsColumns = CustomBreakpointsDetailSheet?.columns as BreakpointColumn[];
    const tmpCustomBreakpoints = isEmpty(breakpointsColumns)
      ? captableInfo?.custom_breakpoints?.breakpoints
      : breakpointsColumns;

    const adjustedBreakPoints = (tmpCustomBreakpoints ?? []).map(
      ({ isDeleteableColumn, ...bp }: BreakpointColumn) => bp
    );
    const gridData = {
      use_custom_breakpoints: !hideGrid,
      custom_breakpoints: {
        breakpoints: adjustedBreakPoints,
        prices_per_share: adjustedBreakPoints?.map((bpCol: BreakpointColumn) => bpCol.price),
      },
    };
    // Store the latest custom breakpoints data in the ref for use in WaterfallBreakpoints if needed
    // eslint-disable-next-line no-param-reassign
    customBreakpointsGridRef.current = gridData;
    return gridData;
  }, [CustomBreakpointsDetailSheet, captableInfo, hideGrid, customBreakpointsGridRef]);

  const saveConfirmation = useCallback(
    callback => {
      if (isAllocationFinal) {
        showDialog({
          wrapper: ConfirmationDialog,
          wrapperProps: {
            title: 'Confirmation',
          },
          content: ConfirmationSaveFinal,
          actions: [
            {
              label: 'Cancel',
              variant: 'text',
            },
            {
              label: 'Save',
              variant: 'contained',
              color: 'primary',
              callback,
            },
          ],
        });
      }
    },
    [isAllocationFinal, showDialog]
  );

  const saveProforma = useCallback(() => {
    const gridData = getGridData();

    saveAsNewProforma({
      ...captableInfo,
      ...gridData,
      name: formState.values.name ?? `${captableInfo!.name} Proforma`,
    } as ProFormaCapTableModel);
  }, [captableInfo, formState.values.name, getGridData, saveAsNewProforma]);

  const handleSave = useCallback((): void | Promise<void> => {
    const saveSideEffects = (): Promise<unknown>[] => {
      const saveTasks: Promise<unknown>[] = [];

      if (!isEmpty(filesToSave)) {
        saveTasks.push(
          addReferenceForExistingDocuments(
            selectedMeasurementDate.id!,
            filesToSave,
            captableId,
            CUSTOM_BREAKPOINTS_REFERENCE_TYPE
          )
        );
      }
      if (!isEmpty(notes) && notesHasChanged) {
        saveTasks.push(
          new Promise<void>((resolve, reject) => {
            saveNotes(undefined, { onSettled: resolve, onError: reject });
          })
        );
      }
      if (!isEmpty(spreadsheetNotes) && spreadsheetNotesHasChanged) {
        saveTasks.push(saveSpreadsheetNotes());
      }

      return saveTasks;
    };

    const cells = CustomBreakpointsDetailSheet?.cells ?? {};
    const cellsToValidate = Object.entries(cells).map(([, cell]) => cell);
    const areCellsInvalid = (cellsToValidate as ICell[]).some(cell => cell.isValid === false);

    if (!CustomBreakpointsDetailSheet?.columns) {
      const saveTasks = saveSideEffects();
      if (saveTasks.length > 0) {
        return Promise.all(saveTasks).then(() => undefined);
      }
      return;
    }

    // Allow save with invalid BPs to disable them.
    if (areCellsInvalid && !hideGrid) {
      saveSideEffects().forEach(task => {
        task.catch(() => undefined);
      });
      setIsAlertVisible(true);
      return;
    }

    const gridData = getGridData() as CapTableDetailsModel;

    if (isAllocationFinal) {
      saveSideEffects().forEach(task => {
        task.catch(() => undefined);
      });
      saveConfirmation(() => {
        updateCustomBreakpoints(captableId, gridData);
        setIsAlertVisible(false);
      });
      return;
    }

    const saveTasks = saveSideEffects();
    saveTasks.push(updateCustomBreakpoints(captableId, gridData));
    setIsAlertVisible(false);
    return Promise.all(saveTasks).then(() => undefined);
  }, [
    CustomBreakpointsDetailSheet,
    addReferenceForExistingDocuments,
    captableId,
    filesToSave,
    getGridData,
    hideGrid,
    isAllocationFinal,
    notes,
    notesHasChanged,
    saveConfirmation,
    saveNotes,
    saveSpreadsheetNotes,
    selectedMeasurementDate.id,
    spreadsheetNotes,
    spreadsheetNotesHasChanged,
    updateCustomBreakpoints,
  ]);

  const savesAsProformaConfirmation = () => {
    setOpenProformaDialog(true);
  };

  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;

  useEffect(() => {
    if (!isEmpty(captableInfo)) {
      onSaveStateChange?.({
        save: () => handleSaveRef.current(),
        disabled: isDisabled,
        locked: isAllocationFinal,
        onProformaOpen: savesAsProformaConfirmation,
        isProformaAvailable: !!captableInfo.is_primary,
      });
    }
    return () => onSaveStateChange?.(null);
  }, [
    captableInfo,
    hideGrid,
    filesToSave,
    CustomBreakpointsDetailSheet,
    notes,
    isDisabled,
    isAllocationFinal,
    onSaveStateChange,
  ]);

  const CustomBreakpointsDetailSheetProps = {
    ...CustomBreakpointsDetailSheet,
    sheet: CustomBreakpointsDetailSheet!,
    workbook,
    onChange,
    page: 'captable',
    className: 'waterfall-custom-width',
    deleteColumn: deleteCustomColumn,
    conditions,
    setIsDisplayingRowNumbers,
    data: CustomBreakpointsDetailSheet?.data ?? [],
  };

  useKeyEventForFabButton(hideGrid);

  const handleOnCloseAlert = useCallback(() => {
    setIsAlertVisible(false);
  }, []);

  const handleOnCloseProformaCapTable = useCallback(() => {
    setOpenProformaDialog(false);
  }, []);

  const handleOnFormChange = useCallback(formValues => {
    setFormState(formValues);
  }, []);

  const renderProformaCaptable = useMemo(
    () => (
      <ProformaCaptableDialog
        editMode={false}
        initialValues={initialFormValues || false}
        isValid={formState?.isValid}
        onClose={handleOnCloseProformaCapTable}
        onFormChange={handleOnFormChange}
        onSave={saveProforma}
        open={openProformaDialog || false}
        tableTerms={{ tableName: 'Cap Table' }}
        title="New Proforma Cap Table"
      />
    ),
    [formState, handleOnCloseProformaCapTable, handleOnFormChange, initialFormValues, openProformaDialog, saveProforma]
  );

  if (!hideGrid && !CustomBreakpointsDetailSheet?.data) {
    return <GridSkeleton />;
  }

  return (
    <>
      <Collapse in={isAlertVisible}>
        <Alert
          variant="outlined"
          severity="error"
          action={
            <IconButton aria-label="close" color="inherit" size="small" onClick={handleOnCloseAlert}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }>
          {FIX_BEFORE_CONTINUE}
        </Alert>
        <br />
      </Collapse>

      {/* Proforma Cap Table */}
      {renderProformaCaptable}

      {!hideGrid && (
        <>
          <ScalarSpreadsheet {...CustomBreakpointsDetailSheetProps} />
          <WidgetDrawer
            documentReferencesProps={{
              selectedMeasurementDate,
              referenceType: CUSTOM_BREAKPOINTS_REFERENCE_TYPE,
              currentPage: CUSTOM_BREAKPOINTS_REFERENCE_TITLE,
              referencedFeatureId: captableId,
              filesToSave,
              setFilesToSave,
              isDisplayingRowNumbers,
              isDisabled,
            }}
            notesProps={{
              pageType: BREAKPOINTS_PAGE_VALUE,
              pageTypeKey: CAP_TABLE_PAGE_KEY,
              pageTypeId: captableId,
              notes,
              setNotes,
              onAddNote,
              onUpdateNotes,
              onDeleteNote,
              isDisabled,
            }}
            spreadsheetNotesProps={{
              pageType: BREAKPOINTS_PAGE_VALUE,
              pageTypeKey: CAP_TABLE_PAGE_KEY,
              pageTypeId: captableId,
              notes: spreadsheetNotes,
              setNotes: setSpreadsheetNotes,
              saveNotes: () => {
                saveSpreadsheetNotes();
                return undefined;
              },
              handleOnDeleteNote: handleOnDeleteSpreadsheetNote,
            }}
          />
        </>
      )}

      <ExtendedFabButton
        addBottomMarginForDrawer
        id="breakpoint-add-btn"
        label="Add Breakpoint"
        onClick={addCustomColumn}
        disabled={hideGrid || isDisabled}
      />
    </>
  );
};

export default CustomBreakpoints;
